import type { Theme } from "components/ui/theme/theme.model";
import { colors, font, radius, motion } from "./tokens";

/**
 * The design tokens, expressed in the shape the vendored `components/ui`
 * ThemeProvider expects.
 *
 * This object is the whole of Phase 1's reach. `components/ui` ships as a
 * compiled bundle with no source, so its internals cannot be restyled — but every
 * one of its components reads `theme.colors.*`, as do the 48 styled-components
 * files in src/. Passing this at the provider repaints all of them at once.
 *
 * ## The brand slots
 *
 * `primary` stays lime and `secondary` stays purple, as the old default theme
 * had them — so every existing call site keeps its meaning. What changed is the
 * exact values (`#91a436` -> `#94A639`, `#7C4789` unchanged) and the arrival of
 * the fill ramps.
 *
 * The division between them is not decorative. Lime is 2.70:1 against white, so
 * it can only ever be a fill carrying dark text; purple is 6.74:1, so it is the
 * brand colour that survives as a 1px stroke, a 24px icon or a focus ring. That
 * is why `border.active`, the focus ring and links resolve to `secondary` even
 * though lime leads the brand.
 *
 * ## The grey collapse
 *
 * The legacy theme carried eleven greys. The spec allows six, so the keys keep
 * their names but no longer their spacing:
 *
 *   grey900  #333333  ->  #1F1F1F  ink            (18 of 19 uses are text)
 *   grey800  #666666  ->  #6C6C6D  secondary text (42 of 46 uses are text)
 *   grey700  #979797  ->  #6C6C6D  secondary text (26 of 32 uses are text)
 *   grey600  #9c9c9c  ->  #6C6C6D  secondary text (16 of 17 uses are text)
 *   grey500  #acacac  ->  #6C6C6D  secondary text (10 of 11 uses are text)
 *   grey400  #dfdfdf  ->  #E4E4E4  border         (39 of 40 uses are border/bg)
 *   grey300  #ebebeb  ->  #E4E4E4  border         (all 35 uses are border/bg)
 *   grey200  #f3f3f3  ->  #F2F2F2  subtle fill    (all 19 uses are border/bg)
 *   grey100  #f7f7f7  ->  #F2F2F2  subtle fill    (all 23 uses are bg)
 *   grey50   #fbfbfb  ->  #FBFAFB  panel
 *   grey30   #E7EFF3  ->  #F2F2F2  (unreferenced; kept only to satisfy the type)
 *
 * Collapsing four mid-greys onto one secondary text colour is deliberate: the
 * spec allows exactly two text colours, and `#979797` on white was 2.92:1, below
 * AA. The handful of call sites that used a mid-grey for a border or a background
 * instead of text were repointed at `var(--color-border-strong)` /
 * `var(--color-grey-400)` directly, since no legacy key carries those values.
 *
 * Two tones in the ramp — `#CECECE` and `#9B9B9B` — have no legacy key at all.
 * Reach for them through `tokens.ts` or the CSS custom properties.
 *
 * ## Deliberately left alone
 *
 * `yellow`, `orange`, `blue` and `lightGreen` are one-off accents (one use each)
 * with no counterpart in the spec's status palette. They keep their values until
 * their screens are migrated, rather than being mapped to something approximate.
 * The `typography.header` scale (h1 40px … h6 13px) also stays: the spec's type
 * scale tops out at 20px, and rescaling every heading belongs with the screens
 * that own them, not here.
 */
const hegiasTheme: Theme = {
	colors: {
		primary: colors.primary, // #94A639 — the logo lime, was #91a436
		secondary: colors.secondary, // #7C4789 — the CI purple, unchanged
		tertiary: colors.logoNavy, // #2D5671 — was #005874, a drift of the same navy

		white: colors.surface,
		black: colors.text, // the spec has no pure black

		red: colors.danger,
		yellow: "#ffcc33", // one-off accents, see note above
		orange: "#c18f2f",
		blue: "#57a4ff",
		lightYellow: colors.warningBg,
		lightBlue: colors.infoBg,
		lightGreen: "#61B292",

		grey900: colors.text,
		grey800: colors.textSecondary,
		grey700: colors.textSecondary,
		grey600: colors.textSecondary,
		grey500: colors.textSecondary,
		grey400: colors.border,
		grey300: colors.border,
		grey200: colors.fillSubtle,
		grey100: colors.fillSubtle,
		grey50: colors.bgPanel,
		grey30: colors.fillSubtle,
	},

	typography: {
		text: {
			smaller: font.sizeMicro,
			small: font.sizeCaption,
			normal: font.sizeBody,
			medium: font.sizeTitle,
			big: font.sizeHeading,
		},
		header: {
			h1: "40px",
			h2: "25px",
			h3: "21px",
			h4: "18px",
			h5: "14px",
			h6: "13px",
		},
		fontFamily: {
			default: font.family,
			verdana: "Verdana",
		},
		weight: {
			// Roles, not numbers — the scale shifted one step lighter on
			// 2026-08-22 (§2), so these now resolve to 300 / 300 / 500.
			thin: font.weightLight,
			normal: font.weightRegular,
			bold: font.weightBold,
		},
	},

	size: {
		form: {
			input: 48, // was 50
			button: 48, // was 45
		},
	},

	border: {
		radius: radius.md, // 6px, was 4px
		color: colors.border,
	},

	transition: motion.transitionBase,
};

export default hegiasTheme;
